import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  Invoice,
  Notice,
  NotificationPreferences,
  Paginated,
  SingleResource,
  TenantDashboardPayload,
  TenantProfilePayload,
} from "./types";

// ─── Dashboard ──────────────────────────────────────────────────────────────

export function useTenantDashboardQuery() {
  return useQuery({
    queryKey: ["tenant", "dashboard"],
    queryFn: async () => {
      const { data } = await api.get<TenantDashboardPayload>("/api/tenant/dashboard");
      return data;
    },
    staleTime: 5 * 60 * 1000,
    // Notices (and other dashboard data) can change from outside this
    // tenant's own session — an admin pushing a notice doesn't trigger any
    // client-side cache invalidation here, unlike a tenant's own actions
    // (e.g. recharge). Without this, a tenant with the dashboard already
    // open could sit on stale data indefinitely until they navigate away
    // and back. Matches the same polling pattern already used for the
    // admin dashboard.
    refetchInterval: 60_000,
  });
}

/**
 * GET /api/tenant/wallet/refresh — a live, uncached balance lookup for right
 * after a tenant funds their wallet (bank transfer) or pays a bill, so they
 * don't have to wait out the dashboard's 10-minute resident cache. Patches
 * the wallet slice of the already-cached dashboard payload directly instead
 * of invalidating the whole dashboard query, so the rest of the dashboard
 * (notices, tenancy, etc.) doesn't refetch along with it.
 */
export function useTenantWalletRefreshMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.get<{ data: TenantDashboardPayload["wallet"] }>("/api/tenant/wallet/refresh");
      return data.data;
    },
    onSuccess: (wallet) => {
      qc.setQueryData<TenantDashboardPayload>(["tenant", "dashboard"], (old) =>
        old ? { ...old, wallet } : old
      );
    },
  });
}

// ─── Invoices ───────────────────────────────────────────────────────────────

export interface InvoicesQueryParams {
  page?: number;
  per_page?: number;
  status?: Invoice["status"];
}

export function useTenantInvoicesQuery(params: InvoicesQueryParams = {}) {
  return useQuery({
    queryKey: ["tenant", "invoices", params],
    queryFn: async () => {
      const q: Record<string, string | number> = {};
      if (params.page) q["page"] = params.page;
      if (params.per_page) q["per_page"] = params.per_page;
      if (params.status) q["filter[status]"] = params.status;
      const { data } = await api.get<Paginated<Invoice>>("/api/tenant/invoices", {
        params: q,
      });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useTenantInvoiceQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["tenant", "invoices", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<SingleResource<Invoice>>(`/api/tenant/invoices/${id}`);
      return data.data;
    },
  });
}

// ─── Bills (real IOBOTech data — pay flows straight to IOBOTech's wallet) ───

export interface TenantBill {
  invoice_id: string | null;
  bill_name: string | null;
  bill_type: "rent" | "service_charge" | "diesel" | null;
  amount_kobo: number;
  amount_formatted: string;
  is_paid: boolean;
  due_date: string | null;
  status: string | null;
}

export function useTenantBillsQuery() {
  return useQuery({
    queryKey: ["tenant", "bills"],
    queryFn: async () => {
      const { data } = await api.get<{ data: TenantBill[]; last_refreshed_at: string }>("/api/tenant/bills");
      return data;
    },
  });
}

// Real-time unpaid invoices straight from IOBOTech — sees charges the bill-
// first query above structurally can't (any invoice with no discoverable
// parent bill in IOBOTech's recurring bill catalog, e.g. "installments"-
// product charges). Deliberately separate from useTenantBillsQuery(), not
// cached the same way, since this is what a tenant checks right before
// paying or vending.
export function useTenantUnpaidBillsQuery() {
  return useQuery({
    queryKey: ["tenant", "bills", "unpaid"],
    queryFn: async () => {
      const { data } = await api.get<{ data: TenantBill[] }>("/api/tenant/bills/unpaid");
      return data;
    },
  });
}

export function usePayBillsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceIds: string[]) => {
      const { data } = await api.post<{ message: string }>("/api/tenant/bills/pay", { invoice_ids: invoiceIds });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant", "bills"] }),
  });
}

// ─── Notices ────────────────────────────────────────────────────────────────

export function useTenantNoticesQuery() {
  return useQuery({
    queryKey: ["tenant", "notices"],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Notice>>("/api/tenant/notices", {
        params: { per_page: 50 },
      });
      return data;
    },
  });
}

/**
 * POST /api/tenant/notices/{id}/acknowledge — optimistic update.
 * We flip the local cache entry immediately, then roll back if the server fails.
 */
export function useAcknowledgeNoticeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noticeId: string) => {
      await api.post(`/api/tenant/notices/${noticeId}/acknowledge`);
    },
    onMutate: async (noticeId) => {
      await queryClient.cancelQueries({ queryKey: ["tenant", "notices"] });
      const previous = queryClient.getQueryData<Paginated<Notice>>(["tenant", "notices"]);

      if (previous) {
        queryClient.setQueryData<Paginated<Notice>>(["tenant", "notices"], {
          ...previous,
          data: previous.data.map((n) =>
            n.id === noticeId
              ? { ...n, acknowledged_at: new Date().toISOString(), read_at: n.read_at ?? new Date().toISOString() }
              : n,
          ),
        });
      }

      // Also bump the dashboard's counts if cached. Acknowledging a notice
      // marks it read server-side too, so it drops out of latest_notices
      // (unread-only) the same as an explicit mark-read would.
      const dashboard = queryClient.getQueryData<TenantDashboardPayload>(["tenant", "dashboard"]);
      if (dashboard) {
        const wasUnread = dashboard.latest_notices.some((n) => n.id === noticeId);
        queryClient.setQueryData<TenantDashboardPayload>(["tenant", "dashboard"], {
          ...dashboard,
          pending_mandatory_count: Math.max(0, dashboard.pending_mandatory_count - 1),
          unread_notice_count: wasUnread
            ? Math.max(0, dashboard.unread_notice_count - 1)
            : dashboard.unread_notice_count,
          latest_notices: dashboard.latest_notices.filter((n) => n.id !== noticeId),
        });
      }

      return { previous, dashboard };
    },
    onError: (_err, _noticeId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["tenant", "notices"], context.previous);
      }
      if (context?.dashboard) {
        queryClient.setQueryData(["tenant", "dashboard"], context.dashboard);
      }
    },
    onSettled: () => {
      // Sync with server once the request resolves either way
      queryClient.invalidateQueries({ queryKey: ["tenant", "notices"] });
      queryClient.invalidateQueries({ queryKey: ["tenant", "dashboard"] });
    },
  });
}

/**
 * POST /api/tenant/notices/{id}/read — independent of acknowledgement.
 * Fired when a tenant actually opens/expands a notice. On the dashboard,
 * a read notice disappears from `latest_notices` (that section is
 * unread-only by design); on the full Notices page it stays listed but its
 * `read_at` flips so it no longer renders as new.
 */
export function useMarkNoticeReadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noticeId: string) => {
      await api.post(`/api/tenant/notices/${noticeId}/read`);
    },
    onMutate: async (noticeId) => {
      await queryClient.cancelQueries({ queryKey: ["tenant", "notices"] });
      await queryClient.cancelQueries({ queryKey: ["tenant", "dashboard"] });

      const previousNotices = queryClient.getQueryData<Paginated<Notice>>(["tenant", "notices"]);
      if (previousNotices) {
        queryClient.setQueryData<Paginated<Notice>>(["tenant", "notices"], {
          ...previousNotices,
          data: previousNotices.data.map((n) =>
            n.id === noticeId && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n,
          ),
        });
      }

      const previousDashboard = queryClient.getQueryData<TenantDashboardPayload>(["tenant", "dashboard"]);
      if (previousDashboard) {
        const wasUnread = previousDashboard.latest_notices.some((n) => n.id === noticeId);
        queryClient.setQueryData<TenantDashboardPayload>(["tenant", "dashboard"], {
          ...previousDashboard,
          latest_notices: previousDashboard.latest_notices.filter((n) => n.id !== noticeId),
          unread_notice_count: wasUnread
            ? Math.max(0, previousDashboard.unread_notice_count - 1)
            : previousDashboard.unread_notice_count,
        });
      }

      return { previousNotices, previousDashboard };
    },
    onError: (_err, _noticeId, context) => {
      if (context?.previousNotices) {
        queryClient.setQueryData(["tenant", "notices"], context.previousNotices);
      }
      if (context?.previousDashboard) {
        queryClient.setQueryData(["tenant", "dashboard"], context.previousDashboard);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant", "notices"] });
      queryClient.invalidateQueries({ queryKey: ["tenant", "dashboard"] });
    },
  });
}

// ─── Documents ──────────────────────────────────────────────────────────────

export function useTenantDocumentsQuery() {
  return useQuery({
    queryKey: ["tenant", "documents"],
    queryFn: async () => {
      const { data } = await api.get<Paginated<import("./types").TenantDocument>>("/api/tenant/documents", {
        params: { per_page: 50 },
      });
      return data;
    },
  });
}

export function useTenancyDocumentsQuery() {
  return useQuery({
    queryKey: ["tenant", "tenancy-documents"],
    queryFn: async () => {
      const { data } = await api.get<{ data: import("./types").TenancyDocument[] }>(
        "/api/tenant/tenancy-documents",
      );
      return data;
    },
  });
}

// ─── Notification Preferences ───────────────────────────────────────────────

export function useNotificationPreferencesQuery() {
  return useQuery({
    queryKey: ["tenant", "notification-preferences"],
    queryFn: async () => {
      const { data } = await api.get<{ data: NotificationPreferences }>("/api/tenant/notification-preferences");
      return data.data;
    },
  });
}

export function useUpdateNotificationPreferencesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email_notices?: boolean; muted_categories?: string[] }) => {
      const { data } = await api.patch<{ data: NotificationPreferences }>("/api/tenant/notification-preferences", payload);
      return data.data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["tenant", "notification-preferences"], updated);
    },
  });
}

// ─── Profile ────────────────────────────────────────────────────────────────

export function useTenantProfileQuery() {
  return useQuery({
    queryKey: ["tenant", "profile"],
    queryFn: async () => {
      const { data } = await api.get<TenantProfilePayload>("/api/tenant/profile");
      return data;
    },
  });
}

// ─── Electricity Vending (IOBOTech-native) ──────────────────────────────────

export interface IOBOTechMeter {
  meter_store_id: string;
  meter_source_id: string;
  meter_number: string | null;
  meter_name: string | null;
  balance: number | null;
  unit: string;
  has_remote_access: boolean;
}

export function useElectricityMetersQuery() {
  return useQuery({
    queryKey: ["tenant", "electricity", "meters"],
    queryFn: async () => {
      const { data } = await api.get<{ data: IOBOTechMeter[] }>("/api/tenant/electricity/meters");
      return data.data;
    },
  });
}

// Bypasses the backend's 10-minute resident cache — for when a tenant wants
// to immediately check whether a newly-assigned/upgraded meter now shows
// remote access, rather than waiting for the cache to expire on its own.
export function useRefreshElectricityMetersMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.get<{ data: IOBOTechMeter[] }>("/api/tenant/electricity/meters", {
        params: { refresh: 1 },
      });
      return data.data;
    },
    onSuccess: (meters) => {
      qc.setQueryData(["tenant", "electricity", "meters"], meters);
    },
  });
}

export interface IOBOTechMeterBalance {
  balance: number | null;
  is_on: boolean | null;
  power: string | null;
  voltage: string | null;
  energy: string | null;
  measurement_unit: string | null;
}

// On-demand only — not every remote-capable meter actually supports this
// call yet (confirmed: hasRemoteAccess alone doesn't guarantee it), so it's
// a manual "Check Balance" action rather than something auto-fetched.
export function useCheckMeterBalanceMutation() {
  return useMutation({
    mutationFn: async (meterNumber: string) => {
      const { data } = await api.get<{ data: IOBOTechMeterBalance }>("/api/tenant/electricity/balance", {
        params: { meter_number: meterNumber },
      });
      return data.data;
    },
  });
}

export function useElectricityRechargeMutation() {
  const qc = useQueryClient();
  return useMutation({
    // A recharge that times out is saved server-side as "being confirmed";
    // refetch the dashboard so that state shows up there too, instead of
    // waiting out its 5 minute cache.
    onSettled: () => qc.invalidateQueries({ queryKey: ["tenant", "dashboard"] }),
    mutationFn: async (payload: {
      amount_kobo: number;
      meter_store_id: string;
      meter_source_id: string;
    }) => {
      const { data } = await api.post<{
        message: string;
        data: { token: string | null; receipt_number: string | null; raw: unknown };
      }>("/api/tenant/electricity/recharge", payload);
      return data;
    },
  });
}

// ─── Transaction Receipts (Phase 8) ─────────────────────────────────────────

export interface TransactionReceipt {
  id: string;
  receipt_number: string;
  transaction_type: "rent" | "service_charge" | "diesel_deposit" | "electricity_vending" | "other";
  amount_kobo: number;
  payment_date: string;
  iobotech_reference?: string;
  notes?: string;
  created_at: string;
}

export interface TransactionHistoryParams {
  page?: number;
  per_page?: number;
  transaction_type?: string;
}

export function useTransactionReceiptsQuery(params: TransactionHistoryParams = {}) {
  return useQuery({
    queryKey: ["tenant", "transactions", params],
    queryFn: async () => {
      const q: Record<string, string | number> = {};
      if (params.page) q["page"] = params.page;
      if (params.per_page) q["per_page"] = params.per_page;
      if (params.transaction_type) q["filter[transaction_type]"] = params.transaction_type;
      const { data } = await api.get<Paginated<TransactionReceipt>>("/api/tenant/transactions", {
        params: q,
      });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useTransactionReceiptQuery(receiptId: string | null) {
  return useQuery({
    queryKey: ["tenant", "transactions", receiptId],
    enabled: !!receiptId,
    queryFn: async () => {
      const { data } = await api.get<{ data: TransactionReceipt }>(`/api/tenant/transactions/${receiptId}`);
      return data.data;
    },
  });
}

export function useDownloadReceiptPdfMutation() {
  return useMutation({
    mutationFn: async (receiptId: string) => {
      const { data } = await api.get(`/api/tenant/transactions/${receiptId}/download-pdf`, {
        responseType: "blob",
      });
      return data;
    },
  });
}
